import React from 'react';
import '../styles/value-propositions.css';

interface ValuePropositionsProps {
  title?: string;
  subtitle?: string;
  description?: string;
  sectionNumber?: string;
}

// Custom SVG Icons
const ScalesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="legal-icon">
    <path d="M12 2L8 6H16L12 2Z" fill="currentColor"/>
    <path d="M6 8V18H8V8H6Z" fill="currentColor"/>
    <path d="M16 8V18H18V8H16Z" fill="currentColor"/>
    <path d="M4 18H20V20H4V18Z" fill="currentColor"/>
    <circle cx="8" cy="22" r="1" fill="currentColor"/>
    <circle cx="16" cy="22" r="1" fill="currentColor"/>
    <path d="M10 12H14V14H10V12Z" fill="currentColor"/>
  </svg>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="legal-icon">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 13H8" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 17H8" stroke="currentColor" strokeWidth="2"/>
    <path d="M10 9H8" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const ScrollIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="legal-icon">
    <path d="M4 6C4 4.9 4.9 4 6 4H18C19.1 4 20 4.9 20 6V18C20 19.1 19.1 20 18 20H6C4.9 20 4 19.1 4 18V6Z" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M8 8H16" stroke="currentColor" strokeWidth="2"/>
    <path d="M8 12H16" stroke="currentColor" strokeWidth="2"/>
    <path d="M8 16H12" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 2L20 6L16 10" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const CourthouseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="legal-icon">
    <path d="M3 21H21" stroke="currentColor" strokeWidth="2"/>
    <path d="M5 21V7L12 3L19 7V21" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M9 9H15" stroke="currentColor" strokeWidth="2"/>
    <path d="M9 13H15" stroke="currentColor" strokeWidth="2"/>
    <path d="M9 17H15" stroke="currentColor" strokeWidth="2"/>
    <circle cx="12" cy="5" r="1" fill="currentColor"/>
  </svg>
);

const ValuePropositions: React.FC<ValuePropositionsProps> = ({
  title = "Value Propositions",
  subtitle = "Why Choose Us",
  description = "We deliver exceptional legal services with proven results and client satisfaction.",
  sectionNumber = "0004"
}) => {
  // Default value propositions data with custom icons
  const valuePropositions = [
    {
      id: "justice-scales",
      title: "Balanced Justice",
      description: "We ensure fair representation and balanced outcomes for all our clients.",
      icon: <ScalesIcon />
    },
    {
      id: "legal-expertise", 
      title: "Expert Legal Counsel",
      description: "Decades of combined experience across multiple practice areas and jurisdictions.",
      icon: <DocumentIcon />
    },
    {
      id: "client-focused",
      title: "Client-Centered Approach", 
      description: "Your success is our priority with personalized attention and strategic guidance.",
      icon: <ScrollIcon />
    },
    {
      id: "proven-results",
      title: "Proven Track Record",
      description: "Consistent success in achieving favorable outcomes for our clients.",
      icon: <CourthouseIcon />
    }
  ];

  return (
    <div className="value-propositions-container">
      {/* Header Section */}
      <div className="value-propositions-header">
        <div className="value-propositions-subtitle-section">
          <h1 className="value-propositions-subtitle">{subtitle}</h1>
          <p className="value-propositions-description">{description}</p>
        </div>
      </div>

      {/* Value Propositions List */}
      <div className="value-propositions-list">
        {valuePropositions.map((proposition, index) => (
          <div
            key={proposition.id}
            className="value-proposition-item"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="value-proposition-content">
              <div className="value-proposition-icon">
                {proposition.icon}
              </div>
              <div className="value-proposition-text">
                <h3 className="value-proposition-title">{proposition.title}</h3>
                <p className="value-proposition-description">{proposition.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ValuePropositions;

